import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";

export default function Header() {
    const { count } = useCart();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const { settings } = useSettings();
    const hiddenPages = settings.hidden_pages || [];
    const links = [
        { to: "/gallery", label: "Prints", page: "gallery" },
        { to: "/originals", label: "Originals", page: "originals" },
        { to: "/commissions", label: "Commissions", page: "commissions" },
        { to: "/about", label: "About", page: "about" },
    ].filter((l) => !hiddenPages.includes(l.page));

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#FAFAF7]/95 backdrop-blur-xl border-b border-black/[0.06]" : "bg-transparent"}`}
            data-testid="site-header"
        >
            <div className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 sm:gap-3 leading-none" data-testid="logo-link">
                    <img
                        src="/brand/logo.png"
                        alt="Art N Moore logo"
                        className="w-12 h-12 sm:w-20 sm:h-20 object-contain shrink-0"
                        data-testid="site-logo-mark"
                    />
                    <div className="flex flex-col">
                        <span className="font-wordmark text-xl sm:text-[26px] font-medium text-[#1A1A1A] leading-none">
                            Art N Moore
                        </span>
                        <span className="mt-1 font-signature text-sm text-[#1A1A1A] leading-none">
                            By Nick Moore
                        </span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `nav-link text-sm tracking-wide text-[#5A5A5A] hover:text-[#1A1A1A] ${isActive ? "active text-[#1A1A1A]" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-6">
                    <Link
                        to="/cart"
                        className="relative flex items-center gap-2 text-[#5A5A5A] hover:text-[#1A1A1A] transition-colors"
                        data-testid="cart-link"
                    >
                        <ShoppingBag className="w-5 h-5" />
                        {count > 0 && (
                            <span
                                className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-[#8C3A3A] text-[10px] flex items-center justify-center text-white"
                                data-testid="cart-count"
                            >
                                {count}
                            </span>
                        )}
                    </Link>
                    <button
                        className="md:hidden text-[#1A1A1A]"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden bg-[#FAFAF7]/95 backdrop-blur-xl border-t border-black/[0.06]" data-testid="mobile-menu">
                    <div className="px-6 py-8 flex flex-col gap-6">
                        {links.map((l) => (
                            <Link key={l.to} to={l.to} className="text-[#1A1A1A] font-serif-display text-2xl">
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
